import { FormControl, InputLabel, Select as MuiSelect, MenuItem, FormHelperText, InputAdornment, IconButton, Chip, Box } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import { Add, Cancel } from "@mui/icons-material";

const SelectField = ({ name, label, options, required, disabled, addButton, onChange, multiple = false, size, ...rest }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const handleChange = (e) => {
          field.onChange(e);
          if (onChange) {
            onChange(e);
          }
        };

        const value = field.value === undefined ? (multiple ? [] : "") : field.value;

        return (
          <FormControl fullWidth error={!!fieldState.error} required={required} disabled={disabled} size={size}>
            <InputLabel id={`label-${name}`}>{label}</InputLabel>
            <MuiSelect
              {...field}
              value={value}
              onChange={handleChange}
              labelId={`label-${name}`}
              label={label}
              multiple={multiple}
              endAdornment={
                addButton && (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={addButton.onClick}
                      sx={{ mr: 1 }}
                    >
                      <Add />
                    </IconButton>
                  </InputAdornment>
                )
              }
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  style: {
                    maxHeight: 224,
                    width: 250,
                  },
                },
              }}
              renderValue={
                multiple
                  ? (selected) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          maxHeight: "80px",
                          overflow: "auto",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                            height: "8px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(0,0,0,0.2)",
                            borderRadius: "4px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "rgba(0,0,0,0.05)",
                          },
                        }}
                      >
                        {selected.map((value) => {
                          const selectedOption = options.find(
                            (option) => option.value === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={
                                selectedOption ? selectedOption.label : value
                              }
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              onDelete={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                const newValue = selected.filter(
                                  (item) => item !== value
                                );
                                field.onChange({ target: { value: newValue } });
                                if (onChange) {
                                  onChange({ target: { value: newValue } });
                                }
                              }}
                              deleteIcon={
                                <Cancel
                                  fontSize="small"
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                  }}
                                />
                              }
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )
                  : undefined
              }
              {...rest}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </MuiSelect>
            {fieldState.error && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
};

SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  addButton: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
  }),
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
};

export default SelectField;
